import { useCallback } from 'react'

import { Divider, Tabs, Tooltip } from '@mantine/core'

import { navigate, useLocation } from '@redwoodjs/router'

import AdminContainer from 'src/components/AdminContainer/AdminContainer'

import IndeedOAuthCompletion from './components/IndeedOAuthCompletion'
import BackgroundCheckTab from './tabs/backgroundCheckTab/BackgroundCheckTab'
import CompanyTab from './tabs/companyTab/CompanyTab'
import CustomStepsTab from './tabs/customStepsTab/CustomStepsTab'
import DataMigrationTab from './tabs/dataMigrationTab/DataMigrationTab'
import DocumentPackagesTab from './tabs/documentPackagesTab/DocumentPackagesTab'
import DocumentTemplatesTab from './tabs/documentTemplatesTab/DocumentTemplatesTab'
import EmailTemplateTab from './tabs/emailTemplateTab/EmailTemplateTab'
import JobListingTemplatesTab from './tabs/jobListingTemplatesTab/JobListingTemplatesTab'
import JobSourcesTab from './tabs/jobSourcesTab/JobSourcesTab'
import NoteTemplateTab from './tabs/noteTemplateTab/NoteTemplateTab'
import RulesTab from './tabs/RulesTab/RulesTab'
import TaskTemplatesTab from './tabs/taskTemplatesTab/TaskTemplatesTab'
import TextTemplateTab from './tabs/textTemplateTab/TextTemplateTab'
import TrainingTab from './tabs/trainingTab/TrainingTab'
import UsersTab from './tabs/usersTab/UsersTab'
import VoiceAgentsTab from './tabs/voiceAgentsTab/VoiceAgentsTab'
import VoiceMailTemplateTab from './tabs/voiceMailTemplateTab/VoiceMailTemplateTab'
import WebhooksTab from './tabs/webhooksTab/WebhooksTab'

const SettingsPage = () => {
  const { search } = useLocation()
  const tab = new URLSearchParams(search).get('tab') || 'rules'

  const handleTabChange = useCallback(
    (newTab: string | null) => {
      if (!newTab) return

      const params = new URLSearchParams(search)
      params.set('tab', newTab)
      navigate(`/settings?${params.toString()}`)
    },
    [search]
  )

  return (
    <div className="flex h-full flex-col px-6">
      <IndeedOAuthCompletion />
      <Tabs
        orientation="vertical"
        value={tab}
        onChange={handleTabChange}
        variant="pills"
        defaultValue="rules"
        className="h-full"
        keepMounted={false}
        classNames={{
          list: 'flex flex-col gap-2',
          tab: `[&[data-active]]:text-doubleNickel-gray-700 [&[data-active]]:bg-doubleNickel-gray-100 [&[data-active]]:font-semibold font-medium text-xs text-doubleNickel-gray-500 rounded py-1 px-2 w-[200px]`,
        }}
      >
        <Tabs.List>
          <div className="flex flex-col gap-1 text-xs text-doubleNickel-gray-500">
            <div className="py-1">GENERAL</div>
            <Tabs.Tab value="company">Company</Tabs.Tab>
            <Tabs.Tab value="users">Users</Tabs.Tab>
            <Tabs.Tab value="rules">Automations</Tabs.Tab>
            <Tabs.Tab value="customSteps">Custom Steps</Tabs.Tab>
          </div>

          <Divider />

          <div className="flex flex-col gap-1 text-xs text-doubleNickel-gray-500">
            <div className="py-1">TEMPLATES</div>
            <Tabs.Tab value="document">Document</Tabs.Tab>
            <Tabs.Tab value="email">Email</Tabs.Tab>
            <Tabs.Tab value="jobListingTemplates">Job Listing</Tabs.Tab>
            <Tabs.Tab value="note">Note</Tabs.Tab>
            <Tabs.Tab value="sms">SMS</Tabs.Tab>
            <Tabs.Tab value="task">Task</Tabs.Tab>
            <Tabs.Tab value="voicemail">Voicemail</Tabs.Tab>
          </div>

          <Divider />

          <div className="flex flex-col gap-1 text-xs text-doubleNickel-gray-500">
            <div className="py-1">INTEGRATIONS</div>
            <AdminContainer>
              <Tabs.Tab value="backgroundCheck">
                <div className="flex flex-1 flex-row items-center justify-between gap-2">
                  Background Check
                  <Tooltip label="Admin only">
                    <div className="h-1 w-1 rounded-full bg-doubleNickel-error-400 text-xs" />
                  </Tooltip>
                </div>
              </Tabs.Tab>
            </AdminContainer>
            <Tabs.Tab value="eSignature">E-Signature Packages</Tabs.Tab>
            <AdminContainer>
              <Tabs.Tab value="training">
                <div className="flex flex-1 flex-row items-center justify-between gap-2">
                  Training
                  <Tooltip label="Admin only">
                    <div className="h-1 w-1 rounded-full bg-doubleNickel-error-400 text-xs" />
                  </Tooltip>
                </div>
              </Tabs.Tab>
            </AdminContainer>
            <Tabs.Tab value="jobSources">Job Sources</Tabs.Tab>
            <AdminContainer>
              <Tabs.Tab value="dataMigration">
                <div className="flex flex-1 flex-row items-center justify-between gap-2">
                  Data Migration
                  <Tooltip label="Admin only">
                    <div className="h-1 w-1 rounded-full bg-doubleNickel-error-400 text-xs" />
                  </Tooltip>
                </div>
              </Tabs.Tab>
            </AdminContainer>
            <Tabs.Tab value="webhooks">Webhooks</Tabs.Tab>
          </div>

          <Divider />
          <div className="flex flex-col gap-1 text-xs text-doubleNickel-gray-500">
            <div className="py-1">AGENTS</div>
            <Tabs.Tab value="voiceAgents">Voice Agents</Tabs.Tab>
          </div>
        </Tabs.List>
        <Divider orientation="vertical" className="mx-4" />
        <Tabs.Panel value="rules" className="h-full">
          <RulesTab />
        </Tabs.Panel>
        <Tabs.Panel value="eSignature" className="h-full">
          <DocumentPackagesTab />
        </Tabs.Panel>
        <Tabs.Panel value="document" className="h-full">
          <DocumentTemplatesTab />
        </Tabs.Panel>
        <Tabs.Panel value="email" className="h-full">
          <EmailTemplateTab />
        </Tabs.Panel>
        <Tabs.Panel value="jobListingTemplates" className="h-full">
          <JobListingTemplatesTab />
        </Tabs.Panel>
        <Tabs.Panel value="note" className="h-full">
          <NoteTemplateTab />
        </Tabs.Panel>
        <Tabs.Panel value="sms" className="h-full">
          <TextTemplateTab />
        </Tabs.Panel>
        <Tabs.Panel value="task" className="h-full">
          <TaskTemplatesTab />
        </Tabs.Panel>
        <Tabs.Panel value="voicemail" className="h-full">
          <VoiceMailTemplateTab />
        </Tabs.Panel>
        <Tabs.Panel value="webhooks" className="h-full">
          <WebhooksTab />
        </Tabs.Panel>
        <Tabs.Panel value="users" className="h-full">
          <UsersTab />
        </Tabs.Panel>
        <Tabs.Panel value="company" className="h-full">
          <CompanyTab />
        </Tabs.Panel>
        <AdminContainer>
          <Tabs.Panel value="backgroundCheck" className="h-full">
            <BackgroundCheckTab />
          </Tabs.Panel>
          <Tabs.Panel value="training" className="h-full">
            <TrainingTab />
          </Tabs.Panel>
          <Tabs.Panel value="dataMigration" className="h-full">
            <DataMigrationTab />
          </Tabs.Panel>
        </AdminContainer>
        <Tabs.Panel value="jobSources" className="h-full">
          <JobSourcesTab />
        </Tabs.Panel>
        <Tabs.Panel value="customSteps" className="h-full">
          <CustomStepsTab />
        </Tabs.Panel>
        <Tabs.Panel value="voiceAgents" className="h-full">
          <VoiceAgentsTab />
        </Tabs.Panel>
      </Tabs>
    </div>
  )
}

export default SettingsPage
